import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { supabaseClient } from '../config/supabase';
import useModuleAccess from '../hooks/useModuleAccess';
import { unsubscribeWebPush } from '../lib/webPushSubscription';
import LoginScreen from './LoginScreen';
import ClientAccountDoor from './shared/ClientAccountDoor';
import AppLoadingScreen from './shared/AppLoadingScreen';
import { AUTH_LINK_ERROR, exchangeAuthLink, takeAuthLink } from '../lib/authRecovery';
import OnboardingWizard from './OnboardingWizard';
import TeamsAdminPage from './integrations/teams/TeamsAdminPage';
import TeamSpacesPage from './integrations/teams/TeamSpacesPage';
import { INTEGRATION_KEYS, canManageIntegration, useIntegrationAccess } from '../lib/integrations/integrationGate';
import FirstOwnerSetupWizard from './onboarding/FirstOwnerSetupWizard';
import AccountMenu from './shared/AccountMenu';
import LeftSidebar from './shared/LeftSidebar';
import Dashboard from './dashboard/Dashboard';
import CustomizableDashboard from './dashboard/CustomizableDashboard';
import CrmScreen from './crm/CrmScreen';
import AdminPanelShell from './admin/AdminPanelShell';
import AdminCenterHome from './admin/AdminCenterHome';
import CrmDuplicateReview from './crm/CrmDuplicateReview';
import EngagementsView from './engagements/EngagementsView';
import EventsRoute from './events/EventsRoute';
import EventGuestsRoute from './events/EventGuestsRoute';
import { EventPlanRoute, ProgramsRoute } from './events/foundation/EventsFoundationRoutes';
import EventsSettingsView from './events/settings/EventsSettingsView';
import useEventsPack from './events/useEventsPack';
import { CALENDAR_LABEL } from './events/eventLabels';
import { CompanyWordsProvider } from '../context/CompanyWordsContext';
import { loadBookingWord, resolveCompanyWords } from '../lib/companyWords';
import CompanyWordsSettings from './settings/CompanyWordsSettings';
import DispatcherView from './dispatch/DispatcherView';
import GroupTransfersView from './dispatch/groupTransfers/GroupTransfersView';
import GroupTransferSettings from './dispatch/groupTransfers/GroupTransferSettings';
import { ZonesDriveTimesSettings, TrafficChartSettings } from './dispatch/groupTransfers/DepartureTimingSettings';
import useViewModePreference from './shared/useViewModePreference';
import { SIDEBAR_MODES, SIDEBAR_MODE_KEY, DEFAULT_SIDEBAR_MODE } from './shared/sidebarMode';
import { useDesktopNav } from './shared/navLayout';
import EntitiesView from './entities/EntitiesView';
import { applyBrand } from '../config/brandPalette';
import {
resolveActiveBrand,
resolveActiveSurfaceHex,
resolveEntityDefaultThemeSettings
} from '../config/paletteContrast';
import FinancialsHubView from './financials/FinancialsHubView';
import InvoicingView from './invoicing/InvoicingView';
import AutoBillingView from './invoicing/AutoBillingView';
import PaymentsView from './invoicing/PaymentsView';
import ScheduledPaymentsView from './invoicing/ScheduledPaymentsView';
import TransactionsView from './financials/TransactionsView';
import ChartOfAccountsView from './financials/ChartOfAccountsView';
import IncomeOnlyView from './financials/IncomeOnlyView';
import ExpenseOnlyView from './financials/ExpenseOnlyView';
import ProfitLossView from './financials/ProfitLossView';
import SettingsNavigator from './settings/SettingsNavigator';
import { settingsRedirectRoutes } from './settings/settingsRoutes';
import FinancialsSettingsView from './settings/FinancialsSettingsView';
import StaffRolesView from './settings/StaffRolesView';
import ApplicantPipelineView from './settings/ApplicantPipelineView';
import ClientOnboardingWelcome from './clients/ClientOnboardingWelcome';
import SupportAccessView from './settings/SupportAccessView';
import CalendarConnectionsView from './settings/CalendarConnectionsView';
import EmailChannelSettingsView from './settings/EmailChannelSettingsView';
import CompanySignaturesView from './settings/signatures/CompanySignaturesView';
import NotificationPreferencesSettingsView from './settings/NotificationPreferencesSettingsView';
import BusinessSchedulePage from './admin/BusinessSchedulePage';
import ClientInvitationsSettings from './admin/ClientInvitationsSettings';
import GoLivePanel from './admin/GoLivePanel';
import ModuleTestBanner from './breaker/ModuleTestBanner';
import ReportsLogsSettingsView from './settings/ReportsLogsSettingsView';
import ScheduleCalendarView from './schedule/ScheduleCalendarView';
import RequireModuleAccess from './shared/RequireModuleAccess';
import RouteGuard from './shared/RouteGuard';
import ImportModal from './modals/ImportModal';
import SubAccountModal from './modals/SubAccountModal';
import TransactionModal from './modals/TransactionModal';
import EditAccountModal from './modals/EditAccountModal';
import EditChartAccountModal from './modals/EditChartAccountModal';
import BusinessInfoDropdown from './modals/BusinessInfoDropdown';
import EditClientDropdown from './modals/EditClientDropdown';
import TasksView from './tasks/TasksView';
import DriverApplicationPortal from './driver/DriverApplicationPortal';
import CommunicationsView from './communications/CommunicationsView';
import AppNavigateContext from '../context/AppNavigateContext';
import NotificationCenterBell from './notifications/NotificationCenterBell';
import ConversationAccessPopups from './communications/ConversationAccessPopups';
import { SupportSessionProvider } from '../context/SupportSessionContext';
import SupportSessionBanner from './support/SupportSessionBanner';
import SupportAnnotationOverlay from './support/SupportAnnotationOverlay';
import SupportConsoleView from './support/SupportConsoleView';
import VerificationCodeCard from './shared/VerificationCodeCard';
import FinishTeamInvitation from './shared/FinishTeamInvitation';
import TenantBrand from './shared/TenantBrand';
import ConversationShortLinkPage from './shared/ConversationShortLinkPage';
import { VERSION } from '../config/version';
import { poweredByLine, webVersionLabel, readDeviceBrand, takeWelcomePending, writeDeviceBrand } from '../lib/signInBranding';
import WelcomeSequence from './auth/WelcomeSequence';
import { lastShownBrand } from './auth/useSignInBrand';
import { useBackNavigation, resetInAppHistory } from '../hooks/useBackNavigation';
import { defaultSettings } from '../config/defaultSettings';
import { resolveSignedInTenantUser } from '../lib/authTenantResolution';
import { featureFlags } from '../config/featureFlags';
import { checkDoorAccess, doorBasePath, doorForAddress, wrongDoorMessage } from '../lib/signInDoors';
import { acceptPendingCompanyInvite, clearCompanyCaches, enterCompany, planCompanyLanding } from '../lib/companyChoice';
import { listMyCompanies } from '../lib/activeCompany';
import ChooseCompanyScreen from './auth/ChooseCompanyScreen';
import PlatformLanding from './auth/PlatformLanding';
import CompanySwitcher from './shared/CompanySwitcher';
import { signInErrorMessage } from '../lib/signInErrors';
import AdminAuthenticatorCard from './auth/AdminAuthenticatorCard';
import PlatformRolesView from './admin/PlatformRolesView';
import PlatformPasswordCard from './auth/PlatformPasswordCard';
import { phoneForSave } from '../lib/phoneSave';

const LEGACY_VIEW_STATE_KEYS = [
'intelAgentCurrentView',
'intelAgentFinancialSubView',
'intelAgentSettingsSubView'
];


const AUTH_CALL_TIMEOUT_MS = 8000;
const AUTH_STATE_INITIALIZATION_FALLBACK_MS = AUTH_CALL_TIMEOUT_MS;

function withTimeout(promise, ms = AUTH_CALL_TIMEOUT_MS) {
    let timer;
    return Promise.race([
        promise,
        new Promise((_, reject) => {
            timer = setTimeout(() => reject(new Error('Auth call timed out')), ms);
        }),
    ]).finally(() => clearTimeout(timer));
}

async function readCommandCenterIdentity() {
    const { data, error } = await supabaseClient.rpc('bl407_identity');
    if (error) throw error;
    if (!data || typeof data.mfa_required !== 'boolean' || typeof data.mfa_satisfied !== 'boolean') {
        throw new Error('Admin authenticator requirements could not be verified.');
    }
    return data;
}

// BL-402: an SSO round-trip arms the welcome before it leaves; read that once per page load.
let oauthWelcomeOnLoad;
function oauthWelcomePending() {
if (oauthWelcomeOnLoad === undefined) oauthWelcomeOnLoad = takeWelcomePending();
return oauthWelcomeOnLoad;
}

function loadWelcomeContext() {
return supabaseClient.rpc('bl402_signin_welcome').then(({ data, error }) => {
    if (error) throw error;
    return data;
});
}

function cloneDefaultSettings() {
return JSON.parse(JSON.stringify(defaultSettings));
}

function AdminOnly({ allowed, isUser = false, children }) {
    if (allowed) return children;
    return isUser
        ? <div role="alert" className="p-8 text-center text-gray-600">You don't have access.</div>
        : <Navigate to="/dashboard" replace />;
}

function CRMApp() {
const navigate = useNavigate();
const location = useLocation();
const { allowed: canManageServices, loading: servicesAccessLoading } = useModuleAccess('settings', 'edit');
const routeSegments = location.pathname.split('/').filter(Boolean);
const currentView = routeSegments[0] || 'dashboard';
const currentSubView = routeSegments[1] || null;
const financialSubView = currentView === 'financials' ? currentSubView : null;
const settingsSubView = currentView === 'settings' ? currentSubView : null;
const [authMode, setAuthMode] = useState('login');
const [expiredInvitationUser, setExpiredInvitationUser] = useState(null);
const [authMessage, setAuthMessage] = useState('');
const [authLoading, setAuthLoading] = useState(false);
const [staffProvisionLoading, setStaffProvisionLoading] = useState(false);
const [staffProvisionMessage, setStaffProvisionMessage] = useState('');
const [provisioningInFlight, setProvisioningInFlight] = useState(false);
const [membershipOrgId, setMembershipOrgId] = useState(null);
const [wizardDismissed, setWizardDismissed] = useState(false);
const [newMembershipCreatedThisSession, setNewMembershipCreatedThisSession] = useState(false);
// undefined = not yet loaded from orgs; null = loaded, still owed the BL-170 Gate 5 guided setup.
const [firstOwnerSetupCompletedAt, setFirstOwnerSetupCompletedAt] = useState(undefined);
const [firstOwnerSetupDismissed, setFirstOwnerSetupDismissed] = useState(false);
const resolvingSessionKeyRef = useRef(null);
const authResolutionAttemptRef = useRef(null);
const hasSavedAccountSettingsRef = useRef(false);
const [accounts, setAccounts] = useState([]);
const [subAccounts, setSubAccounts] = useState([]);
const [transactions, setTransactions] = useState([]);
const [chartOfAccounts, setChartOfAccounts] = useState([]);
const [invoices, setInvoices] = useState([]);
const [payments, setPayments] = useState([]);
const [expandedAccounts, setExpandedAccounts] = useState(new Set());
const [user, setUser] = useState(null);
const [clientAccountDoor, setClientAccountDoor] = useState(false);
// Module 19 (BL-380): the Events pack is dark unless both keys are on (capability + an Events entity).
const { enabled: eventsPackEnabled, loading: eventsPackLoading } = useEventsPack(user?.id);
const [isPlatformSuperAdmin, setIsPlatformSuperAdmin] = useState(false);
const [isOrgAdmin, setIsOrgAdmin] = useState(false);
const [isUserRole, setIsUserRole] = useState(false);
// EV-fix-1: Events Settings (/admin/events) admits Tenant Admins only, so the Admin Center menu and overview offer it only to them.
const eventsAdminEnabled = eventsPackEnabled && Boolean(isOrgAdmin);
// Teams PR A: one database answer (public.integration_access) decides every Teams entry point. Admin tile = a Tenant Admin of an
// organization that opted in; personal screens = Teams on AND this person assigned. Closed while loading.
const teamsAccess = useIntegrationAccess(INTEGRATION_KEYS.MICROSOFT_TEAMS, user?.id);
const teamsAdminEnabled = canManageIntegration(teamsAccess) && Boolean(isOrgAdmin);
const [adminAuthenticatorUser, setAdminAuthenticatorUser] = useState(null);
const [platformPasswordUser, setPlatformPasswordUser] = useState(null);
const [platformDoorContext, setPlatformDoorContext] = useState(null);
// BL-408 G2: set only for a person with two or more companies and none chosen in this sign-in session.
const [companyChoice, setCompanyChoice] = useState(null);
const [platformLanding, setPlatformLanding] = useState(null);
const [brands, setBrands] = useState([]);
const [activeBrandId, setActiveBrandId] = useState(() => localStorage.getItem('intelAgentActiveBrand') || '');
const [tenantName, setTenantName] = useState(() => localStorage.getItem('intelAgentTenantName') || '');
// UX pass: the company's word for a booking (Admin Center > Company Words). null = "Engagement".
const [bookingWord, setBookingWord] = useState(null);
const companyWords = useMemo(() => resolveCompanyWords(bookingWord), [bookingWord]);
const [loading, setLoading] = useState(true);
const [passwordRecoveryUser, setPasswordRecoveryUser] = useState(null);
// BL-402 welcome sequence. It may exit only when the logo is finished AND the app is ready; these are
// the "ready" signals: auth resolutions finished, the first data batch, and the dashboard's first data.
const authResolutionsRef = useRef(0);
const [, setAuthResolutionTick] = useState(0);
const [welcome, setWelcome] = useState(() => (oauthWelcomePending() ? { id: 1, authAtStart: 0, initialBrand: readDeviceBrand() } : null));
const [initialDataReady, setInitialDataReady] = useState(false);
const [dashboardReady, setDashboardReady] = useState(false);
const markDashboardReady = useCallback(() => setDashboardReady(true), []);
const [verificationPurpose, setVerificationPurpose] = useState('invite');
const linkExchangeRef = useRef(null);
const verificationGateRef = useRef(null);
const [searchTerm, setSearchTerm] = useState('');
const [statusFilter, setStatusFilter] = useState('All');

// Global back navigation (BL-220)
const { canGoBack, goBack } = useBackNavigation();
// Reset in-app history depth whenever the user logs in (null → truthy).
const prevUserRef = useRef(null);
useEffect(() => {
    if (!prevUserRef.current && user) {
        resetInAppHistory();
    }
    prevUserRef.current = user;
}, [user]);

// Modal states (converted to dropdowns)
const [showImportModal, setShowImportModal] = useState(false);
const [showSubAccountModal, setShowSubAccountModal] = useState(false);
const [showTransactionModal, setShowTransactionModal] = useState(false);
const [showChartOfAccountsModal, setShowChartOfAccountsModal] = useState(false);
const [showEditAccountModal, setShowEditAccountModal] = useState(false);
const [showEditChartAccountModal, setShowEditChartAccountModal] = useState(false);
const [showBusinessInfoModal, setShowBusinessInfoModal] = useState(false);
const [showEditClientModal, setShowEditClientModal] = useState(false);
const [currentAccountForSub, setCurrentAccountForSub] = useState(null);
const [editingSubAccount, setEditingSubAccount] = useState(null);
const [editingTransaction, setEditingTransaction] = useState(null);
const [editingAccount, setEditingAccount] = useState(null);
const [editingChartAccount, setEditingChartAccount] = useState(null);
const [editingClient, setEditingClient] = useState(null);
const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
// Keep the staff scrolling region above a tablet's visual keyboard. The
// viewport listener changes geometry only; input/form state stays mounted.
useEffect(() => {
    const frame = document.querySelector('.ia-staff-frame'), viewport = window.visualViewport;
    if (!frame || !viewport) return;
    const sync = () => {
        if (window.matchMedia?.('(min-width: 768px) and (pointer: coarse)').matches) {
            frame.style.setProperty('--ia-staff-visual-height', `${viewport.height}px`);
        } else frame.style.removeProperty('--ia-staff-visual-height');
    };
    sync();
    viewport.addEventListener('resize', sync);
    window.addEventListener('resize', sync);
    return () => { viewport.removeEventListener('resize', sync); window.removeEventListener('resize', sync); frame.style.removeProperty('--ia-staff-visual-height'); };
}, [user, initialDataReady]);
const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
const toggleMobileMenu = useCallback(() => setMobileMenuOpen((open) => !open), []);
// Sidebar hover fix 2026-10-25: the header only needs its own ☰ when the sidebar is the phone drawer (no rail on screen).
const navDesktop = useDesktopNav();
// UX pass: "Expand on hover" (the flyover rail, default) or "Pinned open", chosen per person in the account menu.
const [sidebarMode, setSidebarMode] = useViewModePreference({ userId: membershipOrgId ? user?.id : null, storageKey: SIDEBAR_MODE_KEY, modes: SIDEBAR_MODES, defaultMode: DEFAULT_SIDEBAR_MODE });
const [sidebarCollapsed, setSidebarCollapsed] = useState(() => sidebarMode !== 'pinned');
useEffect(() => { setSidebarCollapsed(sidebarMode !== 'pinned'); }, [sidebarMode]);
const [chartViewMode, setChartViewMode] = useState('dashboard'); // 'dashboard' or 'table'

// Universal Autosave Notification State
const [showUniversalAutosave, setShowUniversalAutosave] = useState(false);

// ============================================
// ACCOUNT SETTINGS STATE (with localStorage)
// ============================================
const [accountSettings, setAccountSettings] = useState(() => {
// Try to load from localStorage
const saved = localStorage.getItem('intelAgentSettings');
if (saved) {
try {
hasSavedAccountSettingsRef.current = true;
return JSON.parse(saved);
} catch (e) {
console.error('Error parsing saved settings:', e);
}
}
// Return default settings if nothing saved
return cloneDefaultSettings();
});

// ============================================
// localStorage PERSISTENCE EFFECT
// ============================================
useEffect(() => {
// Save settings to localStorage whenever they change
localStorage.setItem('intelAgentSettings', JSON.stringify(accountSettings));
}, [accountSettings]);

// ============================================
// UNIVERSAL AUTOSAVE NOTIFICATION TRIGGER
// ============================================
const triggerUniversalAutosave = () => {
if (accountSettings?.calendar?.showAutosaveNotification !== false) {
setShowUniversalAutosave(true);
setTimeout(() => setShowUniversalAutosave(false), 2000);
}
};
// ============================================
// AUTO THEME DETECTION - Phase 3
// ============================================
const getEffectiveTheme = () => {
// If theme is not 'auto', return the selected theme
if (accountSettings.theme !== 'auto') {
return accountSettings.theme;
}

// Auto theme is enabled
if (!accountSettings.autoTheme || !accountSettings.autoTheme.enabled) {
return 'light'; // Fallback
}

const mode = accountSettings.autoTheme.mode;

if (mode === 'system') {
// Check system preference
if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
return 'dark';
}
return 'light';
} else if (mode === 'time') {
// Check time-based schedule
const now = new Date();
const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
const schedule = accountSettings.autoTheme.timeBasedSchedule;
const darkStart = schedule.darkStart; // e.g., "18:00"
const darkEnd = schedule.darkEnd; // e.g., "06:00"

// Handle overnight period (e.g., 18:00 to 06:00)
if (darkStart > darkEnd) {
// Dark time spans midnight
if (currentTime >= darkStart || currentTime < darkEnd) {
return 'dark';
}
} else {
// Dark time is within same day (unusual but possible)
if (currentTime >= darkStart && currentTime < darkEnd) {
return 'dark';
}
}
return 'light';
}

return 'light'; // Fallback
};

// Use the effective theme throughout the app
const effectiveTheme = getEffectiveTheme();
// Every Admin Center page shares the same shell inputs (Teams PR A added teamsEnabled: the Integrations > Microsoft Teams entry).
const adminShellProps = { theme: effectiveTheme, eventsEnabled: eventsAdminEnabled, eventsLoading: eventsPackLoading, teamsEnabled: teamsAdminEnabled };
// Check authentication
useEffect(() => {
let mounted = true;
if (!linkExchangeRef.current) {
    const token = takeAuthLink();
    if (token !== null) {
        verificationGateRef.current = { pending: true };
        linkExchangeRef.current = exchangeAuthLink(supabaseClient, token);
    }
}
if (linkExchangeRef.current) {
    const exchange = linkExchangeRef.current;
    exchange.then(({ user: linkUser, purpose }) => {
        if (!mounted || linkExchangeRef.current !== exchange) return;
        verificationGateRef.current = { userId: linkUser.id, purpose };
        sessionStorage.setItem('intelAgentVerificationGate', JSON.stringify(verificationGateRef.current));
        if (purpose === 'recovery') sessionStorage.setItem('intelAgentRecoveryUser', linkUser.id);
        setVerificationPurpose(purpose);
        setPasswordRecoveryUser(linkUser);
        setUser(null);
        setExpiredInvitationUser(null);
        setMembershipOrgId(null);
        setIsOrgAdmin(false);
        setIsPlatformSuperAdmin(false);
        setIsUserRole(false);
        setLoading(false);
    }).catch(() => {
        if (!mounted || linkExchangeRef.current !== exchange) return;
        // Keep auth events blocked after a failed exchange, including any old session.
        verificationGateRef.current = { failed: true };
        setPasswordRecoveryUser(null);
        setUser(null);
        setMembershipOrgId(null);
        setAuthMode('login');
        setAuthMessage(AUTH_LINK_ERROR);
        setLoading(false);
    });
}
let authStateInitialized = false;
const authStateFallbackTimer = setTimeout(() => {
    if (authStateInitialized || verificationGateRef.current || resolvingSessionKeyRef.current) return;
    console.warn('Supabase auth state initialization timed out; showing the signed-out fallback.');
    noteAuthResolved();
    resolvingSessionKeyRef.current = null;
    setPasswordRecoveryUser(null);
    setUser(null);
    setMembershipOrgId(null);
    setIsPlatformSuperAdmin(false);
    setIsOrgAdmin(false);
    setIsUserRole(false);
    setLoading(false);
}, AUTH_STATE_INITIALIZATION_FALLBACK_MS);

let subscription;
const { data } = supabaseClient.auth.onAuthStateChange((event, session) => {
    if (event === 'PASSWORD_RECOVERY' && !verificationGateRef.current) {
        verificationGateRef.current = { userId: session?.user?.id, purpose: 'recovery' };
        sessionStorage.setItem('intelAgentVerificationGate', JSON.stringify(verificationGateRef.current));
    }
    setTimeout(async () => {
    if (!mounted) return;
    authStateInitialized = true;
    clearTimeout(authStateFallbackTimer);
    if (event === 'SIGNED_IN' && session?.user) discardStaleVerificationGate(session.user);
    if (verificationGateRef.current?.pending || verificationGateRef.current?.failed) return;
    if (verificationGateRef.current?.userId && verificationGateRef.current.userId !== session?.user?.id) return;
    if (event === 'PASSWORD_RECOVERY') {
        sessionStorage.setItem('intelAgentRecoveryUser', session?.user?.id || '');
        setVerificationPurpose('recovery');
    }
    // onAuthStateChange fires once immediately with whatever session exists at
    // subscribe time (page load) and again on every later sign-in/out -- this one
    // handler now owns the whole resolution path. It used to run alongside a
    // separate checkUser() call fired on mount, and the two raced to resolve the
    // same candidate user: checkUser() would still be mid-flight on the real
    // membership/admin lookup when this handler's own call landed, saw
    // provisioningInFlightRef already set, and took the "someone else is already
    // resolving this" shortcut -- setting the user immediately without waiting for
    // the real gate. That was the flash: a signed-in session rendering the
    // dashboard for a beat before the actual check caught up and (correctly)
    // blocked or redirected. One path, one resolution, no race.
    //
    // That fixed the checkUser()-vs-onAuthStateChange race, but not a second one:
    // on an OAuth redirect landing (Google/Microsoft/Apple), supabase-js's own
    // client fires THIS handler twice for the exact same session -- once as
    // INITIAL_SESSION (already carrying the freshly exchanged session) and again
    // moments later as a separately-scheduled SIGNED_IN notification for that same
    // session. Both calls land here and both call resolveSignedInUser for the same
    // candidate, so the second call hit the identical "already in flight" shortcut
    // and flashed the dashboard again -- no checkUser() involved this time. Track
    // the candidate user id currently being resolved and skip a duplicate call for
    // that same id; only the original call's result matters.
    const candidateUser = session?.user ?? null;
    await resolveAuthSession(candidateUser);
        }, 0);
});
subscription = data?.subscription;
return () => {
    mounted = false;
    if (authResolutionAttemptRef.current) authResolutionAttemptRef.current.cancelled = true;
    clearTimeout(authStateFallbackTimer);
    subscription?.unsubscribe?.();
};
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// Apply dark mode class to document
useEffect(() => {
if (effectiveTheme === 'dark') {
document.documentElement.classList.add('dark');
} else {
document.documentElement.classList.remove('dark');
}
}, [effectiveTheme]);

// Load data
useEffect(() => {
if (user && user.app_access?.command_center_enabled !== false) {
let cancelled = false;
setInitialDataReady(false);
Promise.allSettled([
loadAccounts(),
loadSubAccounts(),
loadTransactions(),
loadChartOfAccounts(),
loadInvoices(),
loadPayments(),
loadBrands()
]).then(() => { if (!cancelled) setInitialDataReady(true); });
return () => { cancelled = true; };
}
}, [user]);

// Apply the active brand's palette — white-label re-skin (falls back to the Flow teal default)
const activeBrand = useMemo(() => resolveActiveBrand(brands, activeBrandId), [brands, activeBrandId]);

useEffect(() => {
applyBrand(activeBrand?.color_palette?.primary || '', effectiveTheme === 'dark');
if (activeBrandId) localStorage.setItem('intelAgentActiveBrand', activeBrandId);
else localStorage.removeItem('intelAgentActiveBrand');
}, [activeBrand, activeBrandId, effectiveTheme]);

useEffect(() => {
if (!activeBrand) return;
setAccountSettings((current) => resolveEntityDefaultThemeSettings(current, activeBrand));
}, [activeBrand]);

useEffect(() => {
const root = document.documentElement;
const surfaceHex = resolveActiveSurfaceHex({
theme: effectiveTheme,
customTheme: accountSettings.customTheme,
activeBrand
});
root.style.setProperty('--surface-canvas', surfaceHex);
}, [effectiveTheme, accountSettings.customTheme, activeBrand]);

useEffect(() => {
let live = true;
setBookingWord(null);
if (!membershipOrgId) return undefined;
loadBookingWord(supabaseClient, membershipOrgId).then((word) => { if (live) setBookingWord(word); });
return () => { live = false; };
}, [membershipOrgId]);

useEffect(() => {
let isCancelled = false;

async function loadFirstOwnerSetupState() {
if (!membershipOrgId) {
setFirstOwnerSetupCompletedAt(undefined);
return;
}

const { data, error } = await supabaseClient
.from('orgs')
.select('first_owner_setup_completed_at')
.eq('id', membershipOrgId)
.maybeSingle();

if (isCancelled) return;

if (error) {
console.error('Unable to load the first-owner setup state:', error);
setFirstOwnerSetupCompletedAt(null);
return;
}

setFirstOwnerSetupCompletedAt(data?.first_owner_setup_completed_at ?? null);
}

loadFirstOwnerSetupState();

return () => {
isCancelled = true;
};
}, [membershipOrgId]);

useEffect(() => {
LEGACY_VIEW_STATE_KEYS.forEach((key) => localStorage.removeItem(key));
}, []);

function clearVerificationGate() {
verificationGateRef.current = null;
linkExchangeRef.current = null;
sessionStorage.removeItem('intelAgentVerificationGate');
sessionStorage.removeItem('intelAgentRecoveryUser');
}

function discardStaleVerificationGate(candidateUser, passwordSignIn = false) {
if (!candidateUser) return verificationGateRef.current;
let gate = verificationGateRef.current;
if (!gate) {
    try {
        gate = JSON.parse(sessionStorage.getItem('intelAgentVerificationGate') || 'null');
    } catch (error) {
        console.warn('Discarding unreadable sign-in verification gate:', error.message);
        clearVerificationGate();
        return null;
    }
}
// verifyOtp emits SIGNED_IN before an active link exchange has supplied its user.
if (gate?.pending && linkExchangeRef.current && !passwordSignIn) return gate;
if (gate && (gate.pending || gate.failed || gate.userId !== candidateUser.id)) {
    clearVerificationGate();
    setPasswordRecoveryUser(null);
    setExpiredInvitationUser(null);
    gate = null;
}
const recoveryUser = sessionStorage.getItem('intelAgentRecoveryUser');
if (recoveryUser && recoveryUser !== candidateUser.id) {
    sessionStorage.removeItem('intelAgentRecoveryUser');
}
return gate;
}

async function resolveAuthSession(candidateUser) {
const candidateKey = candidateUser?.id ?? null;
if (candidateKey && candidateKey === resolvingSessionKeyRef.current) return;
if (authResolutionAttemptRef.current) authResolutionAttemptRef.current.cancelled = true;
const attempt = { cancelled: false, provisioningInFlightRef: { current: false } };
authResolutionAttemptRef.current = attempt;
resolvingSessionKeyRef.current = candidateKey;
try {
    await withTimeout(resolveSignedInUser(candidateUser, attempt));
    if (attempt.cancelled) return;
    const driverOnly = attempt.resolvedUser?.app_access?.command_center_enabled === false
        && attempt.resolvedUser?.app_access?.driver_app_enabled === true;
    if (candidateUser?.email_confirmed_at && attempt.sessionResolved && !driverOnly) {
        let access;
        try {
            const currentDoor = doorForAddress(window.location);
            access = await withTimeout(checkDoorAccess(currentDoor, supabaseClient, candidateUser.id, {
                multiCompany: async () => {
                    if (attempt.multiCompany) return true;
                    try { return (await listMyCompanies(supabaseClient)).length >= 2; } catch (error) { return false; }
                },
            }));
        } catch (accessError) {
            console.error('Unable to verify sign-in door access:', accessError);
            setUser(null);
            setMembershipOrgId(null);
            setAuthMode('login');
            setAuthMessage("We couldn't verify access right now. Check your connection and try again.");
            setWelcome(null);
            setTimeout(() => {
                if (!attempt.cancelled) supabaseClient.auth.signOut();
            }, 0);
            return;
        }
        if (!access.allowed) {
            setUser(null);
            setMembershipOrgId(null);
            setAuthMode('login');
            setWelcome(null);
            if (access.expectedDoor) {
                const currentDoor = doorForAddress(window.location);
                setAuthMessage(wrongDoorMessage(currentDoor, access.expectedDoor));
            } else {
                setAuthMessage("This account doesn't have access through this sign-in. Ask your administrator for an invitation.");
            }
            setTimeout(() => {
                if (!attempt.cancelled) supabaseClient.auth.signOut();
            }, 0);
            return;
        }
        if (attempt.cancelled) return;
        setPlatformDoorContext(access.context || null);
        if (access.context) {
            setMembershipOrgId(access.context.org_id);
            setActiveBrandId(access.context.entity_id);
            setTenantName(access.context.company);
        }
        // BL-408 G2: platform role holders with two or more companies. One company = plan 'single' = nothing here.
        const platformPlan = attempt.platformPlan;
        if (platformPlan?.kind === 'platform') setPlatformLanding({ companies: platformPlan.companies });
        else if ((platformPlan?.kind === 'active' || platformPlan?.kind === 'entered') && !access.context) {
            setMembershipOrgId(platformPlan.orgId);
        }
    }
    if (attempt.resolvedUser && candidateUser?.email_confirmed_at && !verificationGateRef.current) {
        setCompanyChoice(null);
        setUser(attempt.resolvedUser);
    }
    if (candidateUser) {
        try {
            const { data: orgAdmin, error } = await withTimeout(supabaseClient.rpc('is_org_admin'));
            if (error) throw error;
            if (!attempt.cancelled) setIsOrgAdmin(Boolean(orgAdmin));
        } catch (err) {
            console.error('Unable to determine organization admin navigation access:', err);
            if (!attempt.cancelled) setIsOrgAdmin(false);
        }
        try {
            const { data: platformAdmin, error } = await withTimeout(supabaseClient.rpc('is_platform_super_admin'));
            if (error) throw error;
            if (!attempt.cancelled) setIsPlatformSuperAdmin(Boolean(platformAdmin));
        } catch (err) {
            console.error('Unable to determine platform role management access:', err);
            if (!attempt.cancelled) setIsPlatformSuperAdmin(false);
        }
        try {
            const { data: userRole, error } = await withTimeout(supabaseClient.rpc('bl395a_current_member_is_user'));
            if (error) throw error;
            if (!attempt.cancelled) setIsUserRole(Boolean(userRole));
        } catch (err) {
            console.error('Unable to determine organization User access:', err);
            if (!attempt.cancelled) setIsUserRole(false);
        }
    } else {
        setIsOrgAdmin(false);
        setIsPlatformSuperAdmin(false);
        setIsUserRole(false);
    }
} catch (error) {
    if (attempt.cancelled) return;
    console.warn('Sign-in resolution did not finish:', error.message);
    setUser(null);
    setPasswordRecoveryUser(null);
    setExpiredInvitationUser(null);
    setMembershipOrgId(null);
    setIsOrgAdmin(false);
    setIsPlatformSuperAdmin(false);
    setIsUserRole(false);
    setProvisioningInFlight(false);
    setAuthMode('login');
    setAuthMessage("We couldn't verify access right now. Check your connection and try again.");
    attempt.cancelled = true;
} finally {
    if (authResolutionAttemptRef.current === attempt) {
        setLoading(false);
        resolvingSessionKeyRef.current = null;
        noteAuthResolved();
    }
}
}

async function resolveSignedInUser(candidateUser, attempt) {
setClientAccountDoor(false);
setExpiredInvitationUser(null);
if (!candidateUser) {
    setAdminAuthenticatorUser(null);
    setPlatformPasswordUser(null);
    setPlatformDoorContext(null);
    setCompanyChoice(null);
    setPlatformLanding(null);
}
const gate = discardStaleVerificationGate(candidateUser);
if (gate?.pending || gate?.failed) return;
if (candidateUser && gate?.userId === candidateUser.id) {
    verificationGateRef.current = gate;
    const identity = await readCommandCenterIdentity();
    if (attempt.cancelled) return;
    if (['platform_owner','super_admin','platform_admin'].includes(identity?.platform_role)) {
        setUser(null); setMembershipOrgId(null); setPasswordRecoveryUser(null); setWelcome(null);
        if (identity.mfa_satisfied !== true) {
            setPlatformPasswordUser(null);
            setAdminAuthenticatorUser(candidateUser);
        } else {
            setAdminAuthenticatorUser(null);
            setPlatformPasswordUser(candidateUser);
        }
        return;
    }
    setVerificationPurpose(gate.purpose);
    setPasswordRecoveryUser(candidateUser);
    setUser(null); setMembershipOrgId(null);
    return;
}
if (candidateUser?.email_confirmed_at) {
    const purpose = sessionStorage.getItem('intelAgentRecoveryUser') === candidateUser.id ? 'recovery' : 'invite';
    const { data: verification, error: verificationError } = await supabaseClient.rpc('bl392_verification_context', { p_purpose: purpose });
    if (attempt.cancelled || verificationGateRef.current) return;
    if (verificationError) {
        setUser(null); setMembershipOrgId(null); setAuthMode('login');
        setAuthMessage('Unable to verify account access. Please try signing in again.');
        return;
    }
    if (purpose === 'invite' && verification?.invitation_state === 'expired') {
        setExpiredInvitationUser(candidateUser);
        setPasswordRecoveryUser(null); setUser(null); setMembershipOrgId(null);
        return;
    }
    if (verification?.required) {
        setVerificationPurpose(purpose); setPasswordRecoveryUser(candidateUser);
        setUser(null); setMembershipOrgId(null);
        return;
    }
}
setPasswordRecoveryUser(null);
if (!candidateUser) sessionStorage.removeItem('intelAgentRecoveryUser');
if (candidateUser?.email_confirmed_at) {
    const identity = await readCommandCenterIdentity();
    if (attempt.cancelled) return;
    const { data: clientAccount, error: clientAccountError } = await supabaseClient.rpc('m9_staff_client_redirect');
    // During staggered frontend/DB rollout, the existing resolver still enforces all staff access.
    if (clientAccountError && clientAccountError.code !== 'PGRST202') throw clientAccountError;
    if (attempt.cancelled) return;
    if (clientAccount === true) {
        setClientAccountDoor(true); setUser(null); setMembershipOrgId(null); setWelcome(null); setAuthMessage('');
        return;
    }
    if (identity?.mfa_required && identity?.mfa_satisfied !== true) {
        setUser(null); setMembershipOrgId(null); setWelcome(null);
        setPlatformPasswordUser(null);
        setAdminAuthenticatorUser(candidateUser);
        return;
    }
    setAdminAuthenticatorUser(null);
    if (['platform_owner','super_admin','platform_admin'].includes(identity?.platform_role)) {
        if (identity.password_required) {
            setUser(null); setMembershipOrgId(null); setWelcome(null);
            setPlatformPasswordUser(candidateUser);
            return;
        }
        setPlatformPasswordUser(null);
        // BL-408 G3b: platform identities skip the tenant resolver, so a verified invitation into ANOTHER company (they
        // already hold one) is accepted here, after the authenticator. Nothing happens without such an invitation.
        await acceptPendingCompanyInvite(supabaseClient);
        if (attempt.cancelled) return;
        // BL-408 G2: with two or more companies, the door picks one or the platform landing opens (none chosen yet).
        const platformPlan = await planCompanyLanding({ client: supabaseClient, door: doorForAddress(window.location), isPlatform: true });
        if (attempt.cancelled) return;
        attempt.platformPlan = platformPlan;
        // A platform identity never provisions a tenant or accepts an unrelated company invite.
        attempt.sessionResolved = true;
        attempt.resolvedUser = candidateUser;
        setNewMembershipCreatedThisSession(false);
        return;
    }
}
await resolveSignedInTenantUser(candidateUser, {
supabaseClient,
provisioningInFlightRef: attempt.provisioningInFlightRef,
isCurrent: () => !attempt.cancelled && authResolutionAttemptRef.current === attempt,
setUser: (nextUser) => {
    if (nextUser) {
        attempt.sessionResolved = true;
        attempt.resolvedUser = nextUser;
        if (candidateUser?.email_confirmed_at) return;
    }
    if (!verificationGateRef.current) setUser(nextUser);
},
setMembershipOrgId,
setProvisioningInFlight,
setTenantName,
setNewMembershipCreatedThisSession,
setAuthMode,
    setAuthMessage,
onVerificationRequired: (invitedUser) => {
    setVerificationPurpose('invite');
    setPasswordRecoveryUser(invitedUser);
},
onExpiredInvitation: setExpiredInvitationUser,
alertFn: alert,
storage: localStorage,
resolveMultipleCompanies: async (multiUser) => {
    const plan = await planCompanyLanding({ client: supabaseClient, door: doorForAddress(window.location) });
    if (attempt.cancelled) return plan;
    if (plan.kind !== 'single') attempt.multiCompany = true;
    if (plan.kind === 'choose' || plan.kind === 'platform') {
        setCompanyChoice({ companies: plan.companies, notice: plan.notice || null, user: multiUser });
    }
    return plan;
},
});
if (attempt.resolvedUser && candidateUser?.email_confirmed_at
    && attempt.resolvedUser.app_access?.command_center_enabled !== false) {
    const identity = await readCommandCenterIdentity();
    if (attempt.cancelled) return;
    if (identity?.mfa_required && identity?.mfa_satisfied !== true) {
        attempt.resolvedUser = null;
        attempt.sessionResolved = false;
        setUser(null); setMembershipOrgId(null); setWelcome(null);
        setAdminAuthenticatorUser(candidateUser);
    }
}
}

function noteAuthResolved() {
authResolutionsRef.current += 1;
setAuthResolutionTick(authResolutionsRef.current);
}

function startWelcome() {
setDashboardReady(false);
setWelcome({ id: Date.now(), authAtStart: authResolutionsRef.current, initialBrand: lastShownBrand() });
}

function handleWelcomeExitStart() {
const root = document.getElementById('root');
if (!root) return;
root.classList.add('ia-app-entering');
setTimeout(() => root.classList.remove('ia-app-entering'), 800);
}

async function checkUser() {
let user = null;
    try {
        const { data } = await withTimeout(supabaseClient.auth.getUser());
        user = data?.user ?? null;
    } catch (err) {
        setUser(null);
        setExpiredInvitationUser(null);
        setLoading(false);
        return;
    }
if (user?.user_metadata?.org_name) {
setTenantName(user.user_metadata.org_name);
localStorage.setItem('intelAgentTenantName', user.user_metadata.org_name);
}
await resolveAuthSession(user);
}
async function handleLogin(email, password) {
setAuthLoading(true);
setAuthMessage('');
let data;
let error;
try {
    ({ data, error } = await withTimeout(supabaseClient.auth.signInWithPassword({ email, password })));
} catch (err) {
    setAuthLoading(false);
    setAuthMessage(signInErrorMessage(err));
    return;
}
setAuthLoading(false);
if (error) setAuthMessage(signInErrorMessage(error));
else {
    discardStaleVerificationGate(data?.user, true);
    startWelcome();
    await resolveAuthSession(data?.user ?? null);
}
}
async function handleSignup({ orgName, ownerName, email, password, website, elapsedMs }) {
if (website && website.trim().length > 0) {
setAuthMessage('Signup request received.');
return;
}
if (typeof elapsedMs === 'number' && elapsedMs < 2000) {
setAuthMessage('Please review your details and try again.');
return;
}
setAuthLoading(true);
setAuthMessage('');
const { data, error } = await supabaseClient.auth.signUp({
email,
password,
options: {
data: {
org_name: orgName,
owner_name: ownerName
}
}
});
setAuthLoading(false);
if (error) {
alert('Signup failed: ' + error.message);
return;
}
if (data?.user?.email_confirmed_at) {
setAuthMessage('Account created. You can sign in now.');
setAuthMode('login');
} else {
setAuthMessage('Check your email to verify your account.');
}
}

async function handleStaffProvision({ orgName, email }) {
    setStaffProvisionLoading(true);
    setStaffProvisionMessage('');
    const { data: sessionData } = await supabaseClient.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) {
        setStaffProvisionLoading(false);
        setStaffProvisionMessage('You must be signed in to send an invite.');
        return;
    }
    const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/staff-provision-invite`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({ orgName, email })
    });
    const payload = await response.json();
setStaffProvisionLoading(false);
if (!response.ok) {
setStaffProvisionMessage(payload?.error || 'Invite failed.');
return;
}
setStaffProvisionMessage('Invite sent.');
}

async function handleLogout() {
try {
    await unsubscribeWebPush(supabaseClient, user?.id);
} catch (error) {
    window.alert(error.message);
    return;
}
clearVerificationGate();
if (authResolutionAttemptRef.current) authResolutionAttemptRef.current.cancelled = true;
authResolutionAttemptRef.current = null;
resolvingSessionKeyRef.current = null;
try {
    await withTimeout(supabaseClient.auth.signOut());
} catch (err) {
    // Ignore timeout/errors here so the UI always clears local session state below.
}
setUser(null);
setPasswordRecoveryUser(null);
setExpiredInvitationUser(null);
setMembershipOrgId(null);
setCompanyChoice(null);
setPlatformLanding(null);
setIsOrgAdmin(false);
setIsPlatformSuperAdmin(false);
setIsUserRole(false);
setTenantName('');
setBookingWord(null);
localStorage.removeItem('intelAgentTenantName');
localStorage.removeItem('intelAgentScheduleCalendarLabel');
setWizardDismissed(false);
setNewMembershipCreatedThisSession(false);
setFirstOwnerSetupCompletedAt(undefined);
setFirstOwnerSetupDismissed(false);
            // Platform-admin status is session-scoped, same as everything else cleared above --
            // otherwise a stale `true` survives logout and keeps the staff-provision panel showing
            // on the login screen for a session that no longer has an authenticated admin behind it.
            setIsPlatformSuperAdmin(false);
}

// BL-408 G2: enter the chosen company on the server, then reload cleanly (no cache or live subscription carries over).
async function handleChooseCompany(company) {
    const result = await enterCompany(supabaseClient, company, { landingPath: doorBasePath(window.location) });
    if (result.ok) return result;
    if (result.reason === 'sign_in_again') {
        await handleLogout();
    } else if (result.reason === 'mfa_required' && companyChoice?.user) {
        // The authenticator opens first; when it is done the sign-in re-runs and this list comes back.
        setAdminAuthenticatorUser(companyChoice.user);
    }
    return result;
}

function handleOpenPlatformLink(path) {
    // The platform admin pages need no company. Nothing per-company is cached on the platform side.
    clearCompanyCaches();
    setPlatformLanding(null);
    navigate(path);
}

async function handleFirstOwnerSetupComplete() {
setFirstOwnerSetupDismissed(true);
setFirstOwnerSetupCompletedAt((current) => current || new Date().toISOString());
navigate('/dashboard');
}

async function handlePasswordRecoveryComplete() {
clearVerificationGate();
setPasswordRecoveryUser(null);
await checkUser();
}

const onboardingState = useMemo(() => {
if (!user) {
return { shouldShow: false };
}

return {
shouldShow: newMembershipCreatedThisSession && !wizardDismissed && Boolean(user?.email_confirmed_at)
};
}, [newMembershipCreatedThisSession, user, wizardDismissed]);

// BL-170 Gate 5: fires once, immediately after Gate 4's wizard is dismissed, only for a
// brand-new org whose orgs.first_owner_setup_completed_at is still NULL. Existing tenants
// (backfilled non-null by migration 091) and later logins (newMembershipCreatedThisSession
// resets to false) never see it.
const firstOwnerSetupState = useMemo(() => {
if (!user || !newMembershipCreatedThisSession || !wizardDismissed || firstOwnerSetupDismissed) {
return { shouldShow: false, loading: false };
}
if (firstOwnerSetupCompletedAt === undefined) {
return { shouldShow: false, loading: true };
}
return { shouldShow: firstOwnerSetupCompletedAt === null, loading: false };
}, [user, newMembershipCreatedThisSession, wizardDismissed, firstOwnerSetupDismissed, firstOwnerSetupCompletedAt]);

// Gate 4 finished and this org already had its Gate 5 guided setup — nothing left to show,
// so land on the dashboard the same way Gate 4/5 completion does when they DO run.
useEffect(() => {
if (
wizardDismissed &&
newMembershipCreatedThisSession &&
!firstOwnerSetupDismissed &&
firstOwnerSetupCompletedAt !== undefined &&
firstOwnerSetupCompletedAt !== null
) {
setFirstOwnerSetupDismissed(true);
navigate('/dashboard');
}
}, [wizardDismissed, newMembershipCreatedThisSession, firstOwnerSetupDismissed, firstOwnerSetupCompletedAt, navigate]);

const businessInfoInitialData = useMemo(() => ({}), []);
// ============================================
// DATA LOADING FUNCTIONS
// ============================================
async function loadAccounts() {
const { data, error } = await supabaseClient
.from('accounts')
.select('*')
.order('created_at', { ascending: false });
if (!error) setAccounts(data || []);
}
async function loadSubAccounts() {
const { data, error } = await supabaseClient
.from('sub_accounts')
.select('*')
.order('created_at', { ascending: false });
if (!error) setSubAccounts(data || []);
}
async function loadTransactions() {
const { data, error } = await supabaseClient
.from('transactions')
.select('*')
.order('date', { ascending: false });
if (!error) setTransactions(data || []);
}
async function loadChartOfAccounts() {
const { data, error} = await supabaseClient
.from('transaction_categories')
.select('*')
.order('name');
if (!error) {
const accounts = data || [];
setChartOfAccounts(accounts);
// Store in localStorage for widget access
localStorage.setItem('intelAgentChartOfAccounts', JSON.stringify(accounts));
}
}
async function loadInvoices() {
console.log('[loadInvoices] Loading invoices from Supabase...');
const { data, error } = await supabaseClient
.from('invoices')
.select('*')
.order('created_at', { ascending: false });
if (error) {
console.error('[loadInvoices] Error loading invoices:', error);
} else {
console.log('[loadInvoices] Successfully loaded invoices:', data?.length || 0, 'records');
console.log('[loadInvoices] Invoice data:', data);
setInvoices(data || []);
}
}
async function loadPayments() {
const { data, error } = await supabaseClient
.from('payments')
.select('*')
.order('payment_date', { ascending: false });
if (error) {
console.error('[loadPayments] Error loading payments:', error);
} else {
setPayments(data || []);
}
}
async function loadBrands() {
// All compartments — the palette switcher offers any that carry a color.
const { data, error } = await supabaseClient
.from('entities')
.select('id, name, color_palette, custom, parent_entity_id')
.order('name');
if (!error) setBrands(data || []);
}
// After an entity is saved in Organization: refresh the switcher, and if that
// compartment just got a color, make it the active palette so the re-skin is immediate.
function handleBrandsChanged(savedEntity) {
loadBrands();
if (savedEntity && savedEntity.color_palette?.primary) {
setActiveBrandId(savedEntity.id);
applyBrand(savedEntity.color_palette.primary, effectiveTheme === 'dark');
}
}
// ============================================
// TRANSACTION CRUD OPERATIONS
// ============================================
async function addTransaction(transactionData) {
// Core required fields
const coreData = {
date: transactionData.date,
description: transactionData.description,
type: transactionData.type,
amount: parseFloat(transactionData.amount) || 0
};

// Additional validation
if (isNaN(coreData.amount)) {
alert('Error: Amount must be a valid number');
return;
}

// Optional fields - only include if they have values
const optionalFields = {
merchant: transactionData.merchant,
category: transactionData.category,
memo: transactionData.memo,
tags: transactionData.tags,
from_to: transactionData.fromTo
};

// Add optional fields only if they have values
Object.keys(optionalFields).forEach(key => {
if (optionalFields[key]) {
coreData[key] = optionalFields[key];
}
});

const { error } = await supabaseClient
.from('transactions')
.insert([coreData]);

if (error) {
alert('Error adding transaction: ' + error.message);
} else {
loadTransactions();
triggerUniversalAutosave();
setShowTransactionModal(false);
setEditingTransaction(null);
}
}
async function updateTransaction(id, updates) {
// Core required fields
const coreData = {
date: updates.date,
description: updates.description,
type: updates.type,
amount: parseFloat(updates.amount) || 0
};

// Additional validation
if (isNaN(coreData.amount)) {
alert('Error: Amount must be a valid number');
return;
}

// Optional fields - only include if they have values
const optionalFields = {
merchant: updates.merchant,
category: updates.category,
memo: updates.memo,
tags: updates.tags,
from_to: updates.fromTo
};

// Add optional fields only if they have values
Object.keys(optionalFields).forEach(key => {
if (optionalFields[key]) {
coreData[key] = optionalFields[key];
}
});

const { error } = await supabaseClient
.from('transactions')
.update(coreData)
.eq('id', id);

if (error) {
alert('Error updating transaction: ' + error.message);
} else {
loadTransactions();
triggerUniversalAutosave();
setShowTransactionModal(false);
setEditingTransaction(null);
}
}
async function deleteTransaction(id) {
if (window.confirm('Are you sure you want to delete this transaction?')) {
const { error } = await supabaseClient
.from('transactions')
.delete()
.eq('id', id);

if (error) {
alert('Error deleting transaction: ' + error.message);
} else {
loadTransactions();
}
}
}
function openTransactionModal(transaction = null) {
setEditingTransaction(transaction);
setShowTransactionModal(true);
}
// ============================================
// CHART OF ACCOUNTS CRUD
// ============================================
async function addChartAccount(accountData) {
const { error } = await supabaseClient
.from('transaction_categories')
.insert([accountData]);

if (error) {
alert('Error adding account: ' + error.message);
} else {
loadChartOfAccounts();
triggerUniversalAutosave();
}
}
function openEditChartAccountModal(account) {
setEditingChartAccount(account);
setShowEditChartAccountModal(true);
}
async function updateChartAccount(id, updates) {
const { error } = await supabaseClient
.from('transaction_categories')
.update(updates)
.eq('id', id);

if (error) {
alert('Error updating account: ' + error.message);
} else {
loadChartOfAccounts();
triggerUniversalAutosave();
}
}
async function deleteChartAccount(id) {
if (window.confirm('Are you sure you want to delete this account category?')) {
const { error } = await supabaseClient
.from('transaction_categories')
.delete()
.eq('id', id);

if (error) {
alert('Error deleting account: ' + error.message);
} else {
loadChartOfAccounts();
}
}
}
// ============================================
// EXISTING ACCOUNT FUNCTIONS (Preserved)
// ============================================
async function addAccount(accountData) {
const { error } = await supabaseClient.from('accounts').insert([accountData]);
if (error) alert('Error adding account: ' + error.message);
else {
loadAccounts();
triggerUniversalAutosave();
}
}
function openEditAccountModal(account) {
// Use the new EditClientDropdown instead of the old modal
setEditingClient({
...account,
firstName: account.contact_name?.split(' ')[0] || '',
lastName: account.contact_name?.split(' ')[1] || '',
address: account.address || '',
tags: account.tags || []
});
setShowEditClientModal(true);
}
async function updateAccount(id, updates) {
const { error } = await supabaseClient.from('accounts').update(updates).eq('id', id);
if (error) alert('Error updating account: ' + error.message);
else {
loadAccounts();
triggerUniversalAutosave();
}
}
async function deleteAccount(id) {
if (window.confirm('Are you sure you want to delete this account?')) {
const { error } = await supabaseClient.from('accounts').delete().eq('id', id);
if (error) alert('Error deleting account: ' + error.message);
else loadAccounts();
}
}
async function addSubAccount(subAccountData) {
const { error } = await supabaseClient.from('sub_accounts').insert([subAccountData]);
if (error) alert('Error adding sub-account: ' + error.message);
else {
loadSubAccounts();
triggerUniversalAutosave();
setShowSubAccountModal(false);
setEditingSubAccount(null);
}
}
async function updateSubAccount(id, updates) {
const { error } = await supabaseClient.from('sub_accounts').update(updates).eq('id', id);
if (error) alert('Error updating sub-account: ' + error.message);
else {
loadSubAccounts();
triggerUniversalAutosave();
setShowSubAccountModal(false);
setEditingSubAccount(null);
}
}
async function deleteSubAccount(id) {
if (window.confirm('Are you sure you want to delete this sub-account?')) {
const { error } = await supabaseClient.from('sub_accounts').delete().eq('id', id);
if (error) alert('Error deleting sub-account: ' + error.message);
else loadSubAccounts();
}
}
function toggleAccountExpansion(accountId) {
const newExpanded = new Set(expandedAccounts);
if (newExpanded.has(accountId)) {
newExpanded.delete(accountId);
} else {
newExpanded.add(accountId);
}
setExpandedAccounts(newExpanded);
}
function getSubAccountsForAccount(accountId) {
return subAccounts.filter(sub => sub.account_id === accountId);
}
function openSubAccountModal(account, subAccount = null) {
setCurrentAccountForSub(account);
setEditingSubAccount(subAccount);
setShowSubAccountModal(true);
}
// CSV Import
async function handleCSVImport(csvText) {
const lines = csvText.trim().split('\n');
if (lines.length < 2) {
alert('CSV file appears to be empty or invalid');
return;
}
const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
let imported = 0;
let skipped = 0;
for (let i = 1; i < lines.length; i++) {
const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
if (values.length < 5) {
skipped++;
continue;
}
const rowData = {};
headers.forEach((header, index) => {
rowData[header.toLowerCase().replace(/\s+/g, '_')] = values[index];
});
const companyName = rowData.client ||
(rowData.client_first_name && rowData.client_last_name ?
`${rowData.client_first_name} ${rowData.client_last_name}` : '');

if (!companyName) {
skipped++;
continue;
}
const accountData = {
company_name: companyName,
account: companyName,
contact_name: rowData.client_first_name && rowData.client_last_name ?
`${rowData.client_first_name} ${rowData.client_last_name}` : companyName,
email: rowData.client_email || '',
phone: phoneForSave(rowData.client_phone || ''),
status: rowData.status || 'Lead',
revenue: parseFloat(rowData.open_payments || '0') || 0,
notes: (rowData.notes || '').replace(/<[^>]*>/g, '')
};
const { error } = await supabaseClient.from('accounts').insert([accountData]);
if (error) skipped++;
else imported++;
}
alert(`Import complete!\n\n✅ Successfully imported: ${imported} clients\n⚠️ Skipped: ${skipped} rows`);
loadAccounts();
setShowImportModal(false);
}
// Filter accounts
const filteredAccounts = accounts.filter(account => {
const matchesSearch = !searchTerm ||
(account.account || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
(account.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
(account.contact_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
(account.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
(account.notes || '').toLowerCase().includes(searchTerm.toLowerCase());

const matchesStatus = statusFilter === 'All' || account.status === statusFilter;
return matchesSearch && matchesStatus;
});
// BL-402: (b) of the exit rule. Ready = auth resolved, the first data batch is in, and, when the
// dashboard is what lands, its first data too. "Aborted" = the sign-in didn't end in the app.
const mainAppVisible = Boolean(user) && user.app_access?.command_center_enabled !== false && !platformLanding
&& !onboardingState.shouldShow && !firstOwnerSetupState.shouldShow && !firstOwnerSetupState.loading;
const welcomeReady = !loading && Boolean(user)
&& (!mainAppVisible || (initialDataReady && (currentView !== 'dashboard' || dashboardReady)));
const welcomeAborted = Boolean(welcome) && !loading && !user
&& (Boolean(passwordRecoveryUser) || authResolutionsRef.current > welcome.authAtStart);
const withWelcome = (screen) => (
<>
{screen}
{welcome && (
<WelcomeSequence
key={welcome.id}
ready={welcomeReady}
aborted={welcomeAborted}
loadContext={loadWelcomeContext}
user={user}
initialBrand={welcome.initialBrand}
onContext={writeDeviceBrand}
onExitStart={handleWelcomeExitStart}
onDone={() => setWelcome(null)}
/>
)}
</>
);
if (loading) {
return withWelcome(<AppLoadingScreen />);
}
if (clientAccountDoor) return <ClientAccountDoor onSignOut={() => { setClientAccountDoor(false); handleLogout(); }} />;
if (!user) {
// These sign-in follow-up screens wear the door's own brand; only a tenant door reads the tenant's branding.
const signInDoor = doorForAddress(window.location);
const doorPlatformName = signInDoor.slug ? null : signInDoor.identity.signInHeading;
if (platformPasswordUser) {
return (
<div className="tenant-sign-in flex min-h-screen items-center justify-center p-6">
<div className="w-full max-w-md"><TenantBrand platformName={doorPlatformName}/>
<PlatformPasswordCard key={platformPasswordUser.id} client={supabaseClient}
    onDone={async () => { clearVerificationGate(); setPlatformPasswordUser(null); await checkUser(); }}
    onSignOut={handleLogout} />
</div>
<footer className="tenant-sign-in-footer">{poweredByLine(webVersionLabel(VERSION))}</footer>
</div>
);
}
if (adminAuthenticatorUser) {
return (
<div className="tenant-sign-in flex min-h-screen items-center justify-center p-6">
<div className="w-full max-w-md"><TenantBrand platformName={doorPlatformName}/>
<AdminAuthenticatorCard key={adminAuthenticatorUser.id} client={supabaseClient}
    onDone={checkUser} onSignOut={handleLogout} />
</div>
<footer className="tenant-sign-in-footer">{poweredByLine(webVersionLabel(VERSION))}</footer>
</div>
);
}
if (expiredInvitationUser) {
return <FinishTeamInvitation client={supabaseClient} user={expiredInvitationUser} onSignOut={handleLogout} />;
}
if (passwordRecoveryUser) {
return withWelcome(
<div className="tenant-sign-in flex min-h-screen items-center justify-center p-6">
<div className="w-full max-w-md">
<TenantBrand platformName={doorPlatformName} />
<VerificationCodeCard key={`${passwordRecoveryUser.id}:${verificationPurpose}`} user={passwordRecoveryUser}
    purpose={verificationPurpose} onDone={handlePasswordRecoveryComplete} />
</div>
<footer className="tenant-sign-in-footer">{poweredByLine(webVersionLabel(VERSION))}</footer>
</div>
);
}
if (companyChoice) {
return withWelcome(<ChooseCompanyScreen companies={companyChoice.companies} notice={companyChoice.notice}
    platformName={doorPlatformName} footer={poweredByLine(webVersionLabel(VERSION))} theme={effectiveTheme}
    onChoose={handleChooseCompany} onSignOut={handleLogout} />);
}
return withWelcome(<LoginScreen onLogin={handleLogin} onSignup={handleSignup} mode={authMode} setMode={setAuthMode} loading={authLoading} message={authMessage} isPlatformStaff={isPlatformSuperAdmin} onStaffProvision={handleStaffProvision} staffProvisionLoading={staffProvisionLoading} staffProvisionMessage={staffProvisionMessage} leaving={Boolean(welcome)} />);
}
if (user.app_access?.command_center_enabled === false) {
return withWelcome(<div className="min-h-screen bg-[var(--surface-canvas)]">
<header className="flex items-center justify-between p-4 text-[var(--ink)]">
<span>Driver App Access Only</span>
<button className="rounded-full border border-[var(--surface-border)] px-4 py-2" onClick={handleLogout}>Sign Out</button>
</header>
{user.app_access.staff_match
? <p className="p-6 text-[var(--ink)]">Your staff login has Driver App access only. Use the Driver App with the same email and password. Contact your administrator if you need Central Command access.</p>
: user.app_access.driver_app_enabled
? <DriverApplicationPortal user={user} theme={effectiveTheme} mode="status" />
: <p role="alert" className="p-6">App access has been revoked. Contact your organization administrator.</p>}
</div>);
}
if (platformLanding) {
return withWelcome(<PlatformLanding companies={platformLanding.companies} heading={doorForAddress(window.location).identity.signInHeading || 'IntelAgent'}
    footer={poweredByLine(webVersionLabel(VERSION))} theme={effectiveTheme}
    onChoose={handleChooseCompany} onOpenLink={handleOpenPlatformLink} onSignOut={handleLogout} />);
}
if (onboardingState.shouldShow) {
return withWelcome(<OnboardingWizard user={user} orgId={membershipOrgId} onDismiss={() => {
    setWizardDismissed(true);
}} />);
}
if (firstOwnerSetupState.shouldShow) {
return withWelcome(<FirstOwnerSetupWizard user={user} orgId={membershipOrgId} theme={effectiveTheme} onComplete={handleFirstOwnerSetupComplete} />);
}
if (firstOwnerSetupState.loading) {
return withWelcome(<AppLoadingScreen />);
}
return withWelcome(
<SupportSessionProvider membershipOrgId={membershipOrgId}>
<AppNavigateContext.Provider value={navigate}>
<CompanyWordsProvider words={companyWords}>
<div className="ia-staff-frame flex flex-col h-screen overflow-hidden bg-[var(--surface-canvas)]">
<SupportSessionBanner />
<div className="relative flex flex-1 min-h-0 overflow-hidden">
<LeftSidebar
currentView={currentView}
financialSubView={financialSubView}
tenantName={tenantName}
collapsed={sidebarCollapsed}
mobileOpen={mobileMenuOpen}
onMobileToggle={toggleMobileMenu}
onMobileClose={closeMobileMenu}
onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
user={user}
onLogout={handleLogout}
theme={effectiveTheme}
bookingsLabel={companyWords.bookings}
sidebarMode={sidebarMode}
eventsEnabled={eventsPackEnabled}
isPlatformSuperAdmin={isPlatformSuperAdmin}
isOrgAdmin={isOrgAdmin}
isUser={isUserRole}
/>
<div className="min-w-0 flex-1 flex flex-col overflow-hidden">
{platformDoorContext && <div role="status" style={{ paddingTop: 'calc(var(--banner-y) + env(safe-area-inset-top))' }} className="border-b border-[var(--surface-border)] px-3 md:px-6 py-1.5 md:py-2 [--banner-y:0.375rem] md:[--banner-y:0.5rem] break-words text-sm text-[var(--ink)]">
Company context: {platformDoorContext.company}. Existing company permissions still apply.
</div>}
<header style={{ '--ink': '#FFFFFF', paddingTop: platformDoorContext ? 'var(--header-y)' : 'max(var(--header-y), env(safe-area-inset-top))', paddingLeft: 'max(var(--header-x), env(safe-area-inset-left))', paddingRight: 'max(var(--header-x), env(safe-area-inset-right))' }} className="ia-staff-header border-b border-gray-700 bg-gray-800 px-3 py-2 md:px-6 md:py-[11px] lg:py-4 [--header-x:0.75rem] md:[--header-x:1.5rem] [--header-y:0.5rem] md:[--header-y:11px] lg:[--header-y:1rem]">
<div className="flex justify-between items-center gap-1 md:gap-0">
<div className="min-w-0 flex items-center gap-1 md:gap-3">
{!navDesktop && <button type="button" onClick={() => setMobileMenuOpen(true)} aria-label="Open navigation menu" aria-controls="app-navigation" aria-expanded={mobileMenuOpen} className="flex shrink-0 items-center justify-center min-h-[44px] min-w-[44px] text-2xl text-[var(--ink)]">☰</button>}
{canGoBack && (
  <button
    onClick={goBack}
    aria-label="Go back"
    className="flex shrink-0 items-center gap-1 text-sm font-medium px-2 py-1 min-h-[44px] md:min-h-0 rounded hover:bg-opacity-10 text-gray-300 hover:text-white hover:bg-white"
  >
    ← Back
  </button>
)}
<h1 className="min-w-0 truncate text-lg md:text-2xl font-bold text-[var(--ink)]">
{currentView === 'dashboard' && 'Dashboard'}
{currentView === 'admin' && 'Admin Center'}
{currentView === 'support-console' && 'Support Console'}
{currentView === 'clients' && 'CRM'}
{currentView === 'engagements' && companyWords.bookings}
{currentView === 'schedule-calendar' && CALENDAR_LABEL}
{currentView === 'dispatch' && 'Dispatcher'}
{currentView === 'events' && 'Events'}
{currentView === 'financials' && !financialSubView && 'Financials'}
{currentView === 'financials' && financialSubView === 'invoicing' && 'Invoicing'}
{currentView === 'financials' && financialSubView === 'auto-billing' && 'Auto-Billing'}
{currentView === 'financials' && financialSubView === 'payments' && 'Payments'}
{currentView === 'financials' && financialSubView === 'scheduled-payments' && 'Scheduled Payments'}
{currentView === 'financials' && financialSubView === 'transactions' && 'Transactions'}
{currentView === 'financials' && financialSubView === 'chart-of-accounts' && 'Chart of Accounts'}
{currentView === 'income' && 'Income Transactions'}
{currentView === 'expenses' && 'Expense Transactions'}
{currentView === 'profit-loss' && 'Profit & Loss'}
{currentView === 'tasks' && 'Tasks'}
{currentView === 'my-notifications' && 'My Notifications'}
{currentView === 'settings' && (!settingsSubView || ['profile', 'communications', 'security', 'display', 'my-contacts', 'services'].includes(settingsSubView)) && 'Settings'}
{currentView === 'settings' && settingsSubView === 'financials' && 'Settings / Financials'}
{currentView === 'settings' && settingsSubView === 'organization' && 'Settings / Organization'}
{currentView === 'settings' && settingsSubView === 'roles-permissions' && 'Settings / Roles & Permissions'}
{currentView === 'settings' && settingsSubView === 'support-access' && 'Settings / Support Access'}
{currentView === 'settings' && settingsSubView === 'calendar-connections' && 'Settings / My Calendar Connections'}
{currentView === 'settings' && settingsSubView === 'email-channel' && 'Settings / Communications'}
{currentView === 'settings' && settingsSubView === 'notifications' && 'Settings / Notifications'}
{currentView === 'settings' && settingsSubView === 'reports-logs' && 'Settings / Reports & Logs'}
</h1>
</div>
<div className="flex shrink-0 items-center gap-1 md:gap-4">
<CompanySwitcher client={supabaseClient} userId={user?.id} landingPath={doorBasePath(window.location)} onSignOut={handleLogout} theme={effectiveTheme}
    isPlatform={Boolean(isPlatformSuperAdmin)} onOpenPlatformLink={(path) => navigate(path)} />
<NotificationCenterBell
userId={user?.id}
orgId={membershipOrgId}
theme={effectiveTheme}
/>
<ConversationAccessPopups
client={supabaseClient}
userId={user?.id}
onOpenConversation={(sessionId) => navigate(`/communications?session=${sessionId}`)}
/>
<AccountMenu
user={user}
orgId={membershipOrgId}
tenantName={tenantName}
brands={brands}
activeBrandId={activeBrandId}
onActiveBrandChange={setActiveBrandId}
onOpenMyNotifications={() => navigate('/settings/communications/notifications')}
onLogout={handleLogout}
theme={effectiveTheme}
companyClient={supabaseClient}
landingPath={doorBasePath(window.location)}
sidebarMode={sidebarMode}
onSidebarModeChange={setSidebarMode}
/>
</div>
</div>
</header>
<main className={currentView === 'dashboard' ? 'flex-1 overflow-hidden main-content' : 'flex-1 overflow-auto p-4 main-content bg-[var(--surface-canvas)]'}>
<ModuleTestBanner pathname={location.pathname} isAdmin={isOrgAdmin || isPlatformSuperAdmin} />
<Routes>
<Route path="/" element={<Navigate to="/dashboard" replace />} />
<Route path="/sign-in" element={<Navigate to="/dashboard" replace />} />
<Route path="/dashboard" element={
<CustomizableDashboard
userId={user?.id}
theme={effectiveTheme}
isUser={isUserRole}
onReady={markDashboardReady}
triggerAutosave={triggerUniversalAutosave}
onAction={(action) => {
  if (action === 'create_invoice') {
    navigate('/financials/invoicing?action=create');
  }
  if (action === 'open_communications') {
    navigate('/communications');
  }
}}
/>
} />
{/* Module 2: the CRM screen owns its two sections (Clients + Pipeline) and is
    self-loading — RLS scopes the rows to the caller's book, so no account props are
    drilled in. Filtering by owner in the client would be theatre; the database
    already refuses the rows. The Pipeline section is gated by has_capability. */}
<Route path="/clients" element={isUserRole
    ? <RouteGuard module="crm" action="view" isUser><CrmScreen userId={user?.id} theme={effectiveTheme} isUser /></RouteGuard>
    : <CrmScreen userId={user?.id} theme={effectiveTheme} />} />
<Route
    path="/client-onboarding-preview/:accountId"
    element={featureFlags.bl362ClientOnboardingPreview
        ? <ClientOnboardingWelcome />
        : <Navigate to="/dashboard" replace />}
/>
<Route path="/platform-roles" element={<AdminOnly allowed={isPlatformSuperAdmin}><PlatformRolesView /></AdminOnly>} />
<Route path="/admin" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<AdminCenterHome {...adminShellProps} />
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/organization" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<EntitiesView mode="brands" userId={user?.id} theme={effectiveTheme} onEntitiesChanged={handleBrandsChanged} />
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/financials" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<FinancialsSettingsView theme={effectiveTheme} />
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/personnel" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<StaffRolesView userId={user?.id} theme={effectiveTheme} onEntitiesChanged={handleBrandsChanged} />
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/applicants" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<RouteGuard module="personnel" action="view">
<ApplicantPipelineView userId={user?.id} theme={effectiveTheme} />
</RouteGuard>
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/roles-permissions" element={<Navigate to="/admin/personnel?tab=access" replace />} />
{/* Module 19 G8: Events Settings. Tenant Admins only and only while the Events pack is on; its own tile page (no left panel). */}
<Route path="/admin/events" element={<AdminOnly allowed={isOrgAdmin} isUser={isUserRole}>
<EventsSettingsView userId={user?.id} orgId={membershipOrgId} enabled={eventsPackEnabled} loading={eventsPackLoading} isOrgAdmin={isOrgAdmin} theme={effectiveTheme} />
</AdminOnly>} />
<Route path="/admin/events/:tile" element={<AdminOnly allowed={isOrgAdmin} isUser={isUserRole}>
<EventsSettingsView userId={user?.id} orgId={membershipOrgId} enabled={eventsPackEnabled} loading={eventsPackLoading} isOrgAdmin={isOrgAdmin} theme={effectiveTheme} />
</AdminOnly>} />
{/* Teams PR A: Admin Center > Integrations > Microsoft Teams. Tenant Admins only; the page re-asks the database and every write is refused for anyone else. Own page, no left panel. */}
<Route path="/admin/integrations/microsoft-teams" element={<AdminOnly allowed={isOrgAdmin} isUser={isUserRole}>
<TeamsAdminPage userId={user?.id} orgId={membershipOrgId} theme={effectiveTheme} />
</AdminOnly>} />
<Route path="/admin/notifications" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<NotificationPreferencesSettingsView theme={effectiveTheme} orgId={membershipOrgId} userId={user?.id} />
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/email-channel" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<EmailChannelSettingsView theme={effectiveTheme} orgId={membershipOrgId} />
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/business-schedule" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<BusinessSchedulePage orgId={membershipOrgId} />
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/go-live" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<GoLivePanel />
</AdminPanelShell>
</AdminOnly>} />
<Route path="/admin/client-invitations" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<ClientInvitationsSettings orgId={membershipOrgId} isPlatformSuperAdmin={Boolean(isPlatformSuperAdmin)} />
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/signatures" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<CompanySignaturesView
    orgId={membershipOrgId}
    userId={user?.id}
    isAdmin={Boolean(isOrgAdmin || isPlatformSuperAdmin)}
    platformDoorContext={platformDoorContext}
/>
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/calendar-connections" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<CalendarConnectionsView theme={effectiveTheme} />
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/company-words" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<CompanyWordsSettings orgId={membershipOrgId} onSaved={setBookingWord} />
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/reports-logs" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<ReportsLogsSettingsView theme={effectiveTheme} />
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/crm/duplicates" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<CrmDuplicateReview />
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/admin/support-access" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}>
<AdminPanelShell {...adminShellProps}>
<RouteGuard module="support_access" action="manage">
<SupportAccessView userId={user?.id} orgId={membershipOrgId} theme={effectiveTheme} />
</RouteGuard>
</AdminPanelShell>
 </AdminOnly>} />
<Route path="/engagements" element={isUserRole
    ? <RouteGuard module="dispatch" action="view" isUser><EngagementsView userId={user?.id} orgId={membershipOrgId} theme={effectiveTheme} isUser /></RouteGuard>
    : <EngagementsView userId={user?.id} orgId={membershipOrgId} theme={effectiveTheme} />} />
{/* Teams PR A: in-app team governance (Team Owner / Team Admin / Member). Shows only while Teams is on for this person. */}
<Route path="/team-spaces" element={<TeamSpacesPage userId={user?.id} orgId={membershipOrgId} theme={effectiveTheme} />} />
<Route path="/team-spaces/:teamId" element={<TeamSpacesPage userId={user?.id} orgId={membershipOrgId} theme={effectiveTheme} />} />
<Route path="/events" element={<EventsRoute enabled={eventsPackEnabled} loading={eventsPackLoading} userId={user?.id} orgId={membershipOrgId} isUser={isUserRole} />} />
<Route path="/events/:engagementId/guests" element={<EventGuestsRoute enabled={eventsPackEnabled} loading={eventsPackLoading} userId={user?.id} isUser={isUserRole} />} />
<Route path="/events/programs" element={<ProgramsRoute enabled={eventsPackEnabled} loading={eventsPackLoading} isUser={isUserRole} />} />
<Route path="/events/:engagementId/plan" element={<EventPlanRoute enabled={eventsPackEnabled} loading={eventsPackLoading} isUser={isUserRole} />} />
<Route path="/schedule-calendar" element={
<RouteGuard module="dispatch" action="view" isUser={isUserRole}>
<ScheduleCalendarView theme={effectiveTheme} accountSettings={accountSettings} scheduleCalendarLabel={CALENDAR_LABEL} isUser={isUserRole} />
</RouteGuard>
} />
<Route path="/dispatch" element={
<RouteGuard module="dispatch" action="view" isUser={isUserRole}>
<DispatcherView section="board" userId={user?.id} theme={effectiveTheme} isUser={isUserRole} />
</RouteGuard>
} />
<Route path="/dispatch/group-transfers" element={<RouteGuard module="dispatch" action="view" isUser={isUserRole}><GroupTransfersView /></RouteGuard>} />
<Route path="/admin/dispatch/group-transfers" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}><GroupTransferSettings /></AdminOnly>} />
<Route path="/admin/dispatch/zones" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}><ZonesDriveTimesSettings /></AdminOnly>} />
<Route path="/admin/dispatch/traffic-chart" element={<AdminOnly allowed={isOrgAdmin || isPlatformSuperAdmin} isUser={isUserRole}><TrafficChartSettings /></AdminOnly>} />
<Route path="/dispatch/affiliates" element={
<RouteGuard module="dispatch" action="view" isUser={isUserRole}>
<DispatcherView section="affiliates" userId={user?.id} theme={effectiveTheme} isUser={isUserRole} />
</RouteGuard>
} />
<Route path="/driver/apply" element={<DriverApplicationPortal user={user} theme={effectiveTheme} mode="apply" />} />
<Route path="/driver/application/status" element={<DriverApplicationPortal user={user} theme={effectiveTheme} mode="status" />} />
<Route path="/communications" element={isUserRole
    ? <RouteGuard module="comms" action="view" isUser><CommunicationsView theme={effectiveTheme} orgId={membershipOrgId} platformDoorContext={platformDoorContext} /></RouteGuard>
    : <CommunicationsView theme={effectiveTheme} orgId={membershipOrgId} platformDoorContext={platformDoorContext} />} />
<Route path="/financials" element={<RouteGuard module="financial" action="view" isUser={isUserRole}><FinancialsHubView theme={effectiveTheme} /></RouteGuard>} />
<Route path="/financials/invoicing" element={<RouteGuard module="financial" action="view" isUser={isUserRole}><InvoicingView invoices={invoices} accounts={accounts} orgId={membershipOrgId} theme={effectiveTheme} isUser={isUserRole} /></RouteGuard>} />
<Route path="/financials/auto-billing" element={<RouteGuard module="financial" action="view" isUser={isUserRole}><AutoBillingView theme={effectiveTheme} isUser={isUserRole} /></RouteGuard>} />
<Route path="/financials/payments" element={<RouteGuard module="financial" action="view" isUser={isUserRole}><PaymentsView payments={payments} accounts={accounts} theme={effectiveTheme} /></RouteGuard>} />
<Route path="/financials/scheduled-payments" element={<RouteGuard module="financial" action="view" isUser={isUserRole}><ScheduledPaymentsView theme={effectiveTheme} /></RouteGuard>} />
<Route path="/financials/transactions" element={
<RouteGuard module="financial" action="view" isUser={isUserRole}>
<TransactionsView
transactions={transactions}
chartOfAccounts={chartOfAccounts}
onAddTransaction={() => openTransactionModal()}
onEditTransaction={openTransactionModal}
onDeleteTransaction={deleteTransaction}
theme={effectiveTheme}
userId={user?.id}
isUser={isUserRole}
/>
</RouteGuard>
} />
<Route path="/financials/chart-of-accounts" element={
<RouteGuard module="financial" action="view" isUser={isUserRole}>
<ChartOfAccountsView
chartOfAccounts={chartOfAccounts}
onAdd={addChartAccount}
onUpdate={openEditChartAccountModal}
onDelete={deleteChartAccount}
theme={effectiveTheme}
userId={user?.id}
isUser={isUserRole}
/>
</RouteGuard>
} />
<Route path="/income" element={
<RouteGuard module="financial" action="view" isUser={isUserRole}>
<IncomeOnlyView
transactions={transactions}
chartOfAccounts={chartOfAccounts}
onAddTransaction={() => openTransactionModal()}
onEditTransaction={openTransactionModal}
onDeleteTransaction={deleteTransaction}
theme={effectiveTheme}
isUser={isUserRole}
/>
</RouteGuard>
} />
<Route path="/expenses" element={
<RouteGuard module="financial" action="view" isUser={isUserRole}>
<ExpenseOnlyView
transactions={transactions}
chartOfAccounts={chartOfAccounts}
onAddTransaction={() => openTransactionModal()}
onEditTransaction={openTransactionModal}
onDeleteTransaction={deleteTransaction}
theme={effectiveTheme}
isUser={isUserRole}
/>
</RouteGuard>
} />
<Route path="/profit-loss" element={
<RouteGuard module="financial" action="view" isUser={isUserRole}>
<ProfitLossView transactions={transactions} theme={effectiveTheme} />
</RouteGuard>
} />
<Route path="/tasks" element={isUserRole ? <RouteGuard module="tasks" action="view" isUser><TasksView userId={user?.id} theme={effectiveTheme} isUser /></RouteGuard> : (
<TasksView userId={user?.id} theme={effectiveTheme} />
)} />
<Route path="/support-console" element={
isPlatformSuperAdmin ? (
<SupportConsoleView user={user} isPlatformSuperAdmin={isPlatformSuperAdmin} theme={effectiveTheme} />
) : (
<Navigate to="/dashboard" replace />
)
} />
{/* BL-177 Step 2j (E): Settings is a grid of 3D tiles that slide into each other (no left menu). Every level has its own URL under /settings/*. */}
<Route path="/settings/*" element={<SettingsNavigator
    isUser={isUserRole}
    isOrgAdmin={Boolean(isOrgAdmin)}
    isPlatformSuperAdmin={Boolean(isPlatformSuperAdmin)}
    canManageServices={!servicesAccessLoading && canManageServices}
    userId={user?.id}
    orgId={membershipOrgId}
    platformDoorContext={platformDoorContext}
    userEmail={user?.email || ''}
    theme={effectiveTheme}
    accountSettings={accountSettings}
    setAccountSettings={setAccountSettings}
    triggerAutosave={triggerUniversalAutosave}
    onThemeSettingsChange={(newSettings) => {
        setAccountSettings({ ...newSettings, themeExplicitlySet: true });
        triggerUniversalAutosave();
    }}
/>} />
{/* The old personal-settings URLs land on their new home (links in emails, push alerts and the account menu still use them). */}
{settingsRedirectRoutes(location.search)}
<Route path="/settings/financials" element={<Navigate to="/admin/financials" replace />} />
<Route path="/settings/roles-permissions" element={<Navigate to="/admin/roles-permissions" replace />} />
<Route path="/settings/personnel" element={<Navigate to="/admin/personnel" replace />} />
<Route path="/settings/applicants" element={<Navigate to="/admin/applicants" replace />} />
<Route path="/settings/support-access" element={<Navigate to="/admin/support-access" replace />} />
<Route path="/settings/calendar-connections" element={<Navigate to="/admin/calendar-connections" replace />} />
<Route path="/settings/email-channel" element={<Navigate to={`/admin/email-channel${location.search}`} replace />} />
<Route path="/settings/notifications" element={<Navigate to="/admin/notifications" replace />} />
<Route path="/settings/reports-logs" element={<Navigate to="/admin/reports-logs" replace />} />
<Route path="/settings/organization" element={<Navigate to="/admin/organization" replace />} />
<Route path="/settings/phone-numbers" element={<Navigate to="/admin/email-channel?tab=numbers" replace />} />
<Route path="/settings/ai-assistant" element={<Navigate to="/admin/business-schedule#ai-integration" replace />} />
<Route path="/settings/templates" element={<Navigate to="/admin/email-channel?tab=templates" replace />} />
<Route path="/settings/customer-consent" element={<Navigate to="/admin/email-channel?tab=customer-consent" replace />} />
{/* BL-177 Step 2d: short link from a staff text alert (signed in here; the DB checks company + access). */}
<Route path="/c/:code" element={<ConversationShortLinkPage />} />
<Route path="*" element={<Navigate to="/dashboard" replace />} />
</Routes>
</main>
</div>
{/* Modals */}
{showImportModal && (
<ImportModal
onClose={() => setShowImportModal(false)}
onImport={handleCSVImport}
/>
)}
{showSubAccountModal && (
<SubAccountModal
account={currentAccountForSub}
subAccount={editingSubAccount}
onClose={() => {
setShowSubAccountModal(false);
setEditingSubAccount(null);
}}
onSave={(data) => {
if (editingSubAccount) {
updateSubAccount(editingSubAccount.id, data);
} else {
addSubAccount({ ...data, account_id: currentAccountForSub.id });
}
}}
/>
)}
{showTransactionModal && (
<TransactionModal
transaction={editingTransaction}
chartOfAccounts={chartOfAccounts}
onClose={() => {
setShowTransactionModal(false);
setEditingTransaction(null);
}}
onSave={(data) => {
if (editingTransaction) {
updateTransaction(editingTransaction.id, data);
} else {
addTransaction(data);
}
}}
/>
)}
{showEditAccountModal && (
<EditAccountModal
account={editingAccount}
onClose={() => {
setShowEditAccountModal(false);
setEditingAccount(null);
}}
onSave={(data) => {
updateAccount(editingAccount.id, data);
setShowEditAccountModal(false);
setEditingAccount(null);
}}
/>
)}
{showEditChartAccountModal && (
<EditChartAccountModal
account={editingChartAccount}
onClose={() => {
setShowEditChartAccountModal(false);
setEditingChartAccount(null);
}}
onSave={(data) => {
updateChartAccount(editingChartAccount.id, data);
setShowEditChartAccountModal(false);
setEditingChartAccount(null);
}}
/>
)}
{showBusinessInfoModal && (
<BusinessInfoDropdown
onClose={() => setShowBusinessInfoModal(false)}
initialData={businessInfoInitialData}
onSave={() => {
// UX pass: the calendar is called "Calendar" for every company, so this form no longer saves a calendar name.
setShowBusinessInfoModal(false);
}}
/>
)}
{showEditClientModal && (
<EditClientDropdown
client={editingClient}
onClose={() => {
setShowEditClientModal(false);
setEditingClient(null);
}}
onSave={(data) => {
// Update client in Supabase
if (editingClient) {
updateAccount(editingClient.id, data);
}
setShowEditClientModal(false);
setEditingClient(null);
}}
/>
)}

{/* Universal Autosave Notification */}
{showUniversalAutosave && (
<div className="autosave-notification">
Changes Saved
</div>
)}
</div>
<SupportAnnotationOverlay />
</div>
</CompanyWordsProvider>
</AppNavigateContext.Provider>
</SupportSessionProvider>
);
}

export default CRMApp;
